import type { LoginRequest } from '@ghp/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { closeSession, openSession, sessionQuery, SIGNED_OUT, type AuthApi } from './session'

/**
 * La session vue par React — TanStack Query et rien d'autre (D21) : la clé `['auth',
 * 'session']` est **l'unique** état de connexion de l'app, lue par les gardes de route comme
 * par les écrans. Pas de store maison ici, contrairement au terrain, dont la session doit
 * survivre à un rechargement hors réseau et vit donc en IndexedDB (D22).
 *
 * `signIn` et `signOut` sont des mutations pour ce qu'elles apportent gratuitement à l'écran :
 * l'état en cours et l'échec. Ce sont bien des **écritures serveur** — le back-office écrit,
 * il ne propose pas (D4).
 *
 * ⚠️ `session` n'a pas encore de consommateur d'écran : le jeton d'accès est ce que liront les
 * appels authentifiés au câblage D21, et **l'identité affichable n'existe pas côté client** —
 * `/auth/refresh` ne rend qu'un jeton, aucune route ne dit *qui* est connecté. Le bloc
 * utilisateur du prototype (nom, rôle) attend cet endpoint, il ne s'invente pas ici.
 */
export function useAuth(api: AuthApi) {
  const queryClient = useQueryClient()
  const { queryKey } = sessionQuery(api)
  const { data: session } = useQuery(sessionQuery(api))

  const signIn = useMutation({
    mutationFn: (credentials: LoginRequest) => openSession(api, credentials),
    onSuccess: (opened) => queryClient.setQueryData(queryKey, opened),
  })

  const signOut = useMutation({
    mutationFn: () => closeSession(api),
    onSuccess: () => {
      // Tout le cache de lecture part avec la session : ces écrans lisent des données
      // internes servies par l'API (D24), elles n'ont rien à faire dans l'onglet d'une
      // personne déconnectée. `removeQueries` et non `clear` : le second vide **aussi** le
      // cache des mutations — dont celle-ci, en plein `onSuccess`.
      queryClient.removeQueries()
      queryClient.setQueryData(queryKey, SIGNED_OUT)
    },
  })

  return { session: session ?? SIGNED_OUT, signIn, signOut }
}
